/**
 * Shared "interesting wall" heuristic + room-polygon geometry helpers.
 *
 * Extracted from `orbitFraming.js` so BOTH the orbit dollhouse framing and
 * the first-person feature-anchored spawn can pick the same target wall
 * from the same pure code. Nothing here touches THREE — every export is a
 * pure function over plain `{x, z}` / `{x, y, z}` data.
 *
 * Geometry conventions (per CLAUDE.md):
 *   - Floor polygon corners are counter-clockwise viewed from above.
 *   - Increasing Z is screen-down in the top-down floor-plan view.
 */

import { distanceToLineSegment } from '@shared-lib/geometry/pointInPolygon';
import { signedAreaXZ } from '@shared-lib/room-merge/geometry';
import { primaryRoomKind } from '@shared-lib/rooms/roomKind';
import { resolveObjectType } from '../scene/resolveRenderSource';

// Standing eye height (metres). Shared by the first-person spawn (camera Y)
// and any other consumer that needs an eye-level constant — keep ONE source
// so the two never drift. ~4'9"; matches the pre-existing first-person
// `walkingHeight` literal in CameraControls.
export const FIRST_PERSON_EYE_HEIGHT = 1.55;

// --- corner accessors ------------------------------------------------------

export const cornerXZ = (corner) => {
  if (Array.isArray(corner)) {
    return { x: corner[0] ?? 0, z: corner[2] ?? 0 };
  }
  return { x: corner?.x ?? 0, z: corner?.z ?? 0 };
};

export const cornerY = (corner) => {
  if (Array.isArray(corner)) return corner[1] ?? 0;
  return corner?.y ?? 0;
};

export const corners2D = (room) => {
  const raw = room?.floors?.[0]?.polygonCorners || [];
  return raw.map((c) => ({ ...cornerXZ(c), y: cornerY(c) }));
};

/**
 * Floor Y of a room: the first finite corner Y of its floor polygon.
 * Single source so the center-spawn, feature-spawn, and dollhouse framing
 * all agree on floor height.
 */
export const roomFloorY = (room) => {
  const corners = room?.floors?.[0]?.polygonCorners || [];
  for (const c of corners) {
    const y = cornerY(c);
    if (Number.isFinite(y)) return y;
  }
  return 0;
};

// --- polygon / segment geometry --------------------------------------------

export const polygonCenterXZ = (corners) => {
  let sx = 0;
  let sz = 0;
  for (const c of corners) {
    sx += c.x;
    sz += c.z;
  }
  return { x: sx / corners.length, z: sz / corners.length };
};

/**
 * Winding of an X-Z ring: `+1`, `-1`, or `0` when it encloses no area.
 *
 * Under this repo's conventions a floor polygon that is "counter-clockwise
 * viewed from above" has a NEGATIVE shoelace in (x, z), because increasing Z
 * is screen-DOWN. `ensureCounterClockwise`
 * (`functions/features/element-processing.js`) is the definition of that
 * convention: it reverses any ring whose signed area comes out positive.
 *
 * `0` is an answer, not a failure: a ring enclosing no area has no interior
 * for a normal to point away from, so callers must handle it rather than be
 * handed a guessed side. The threshold matches the degeneracy test in
 * `centroidXZ`, in the same module `signedAreaXZ` comes from (`|2A| < 1e-9`).
 */
export const polygonWindingSign = (corners) => {
  const twiceArea = signedAreaXZ(corners) * 2;
  if (Math.abs(twiceArea) < 1e-9) return 0;
  return twiceArea > 0 ? 1 : -1;
};

/**
 * Outward normal of a segment p0→p1 in the X-Z plane, signed by the ring's
 * WINDING (see `polygonWindingSign`):
 *
 *   outward = (s·dz, −s·dx) / |edge|
 *
 * For the repo's standard negative-shoelace ring that reduces to `(−dz, dx)`
 * — the mirror of the inward normal `(dz, −dx)` derived the same way in
 * `shared-lib/geometry/walkwayCorridors.js`. Carrying `s` instead of assuming
 * the convention keeps this correct for a ring wound either way.
 *
 * This replaces a centroid side test ("outward points away from the polygon
 * centre"), which is only valid for CONVEX polygons. On a real 13-corner
 * kitchen whose vertex-average centre sits in a connecting neck, it flipped 4
 * of 13 normals — including the wall first person spawns against, which put
 * the walker outside the room with every direction blocked (issue #1456).
 * Across the offline corpus it was wrong on 230 segments in 80 of 230 rooms.
 *
 * PRECONDITION: a simple (non-self-intersecting) ring. Winding is undefined
 * for a bowtie, and a handful of real scans do emit self-intersecting floor
 * rings — `seatFirstPersonCamera` is the backstop that stops a bad normal
 * from stranding the camera.
 */
export const segmentOutwardNormal = (p0, p1, windingSign) => {
  const dx = p1.x - p0.x;
  const dz = p1.z - p0.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return { x: 0, z: 0 };
  return { x: (windingSign * dz) / len, z: (-windingSign * dx) / len };
};

export const buildWallSegments = (corners) => {
  // Fewer than 3 corners encloses no area, so no edge has an outward side.
  if (corners.length < 3) return [];
  const windingSign = polygonWindingSign(corners);
  // Same for a zero-area ring. Returning no segments routes callers down the
  // documented `no-walls` path in `pickInterestingWall`, which both consumers
  // already handle, rather than inventing a side for every wall.
  if (windingSign === 0) return [];
  const segs = [];
  for (let i = 0; i < corners.length; i++) {
    const p0 = corners[i];
    const p1 = corners[(i + 1) % corners.length];
    const dx = p1.x - p0.x;
    const dz = p1.z - p0.z;
    const length = Math.hypot(dx, dz);
    if (length < 1e-6) continue;
    const segCenter = { x: (p0.x + p1.x) / 2, z: (p0.z + p1.z) / 2 };
    const normal = segmentOutwardNormal(p0, p1, windingSign);
    segs.push({
      index: i,
      p0,
      p1,
      length,
      center: segCenter,
      normal,
      // Tangent along the wall, used for "perpendicular to wall" depth.
      tangent: { x: dx / length, z: dz / length },
    });
  }
  return segs;
};

/**
 * Project a point onto the infinite line through a segment; return the
 * orthogonal distance.
 */
export const distanceToSegmentLine = (segment, point) => {
  const { p0, tangent } = segment;
  const vx = point.x - p0.x;
  const vz = point.z - p0.z;
  // Cross product magnitude of (p - p0) and tangent gives orthogonal dist.
  return Math.abs(vx * tangent.z - vz * tangent.x);
};

/**
 * Project a point onto the infinite line through a wall segment (X-Z).
 *
 * Both camera paths anchor on a fixture centroid that sits a little off its
 * wall; projecting it onto the wall line keeps that depth from skewing the
 * standing point sideways or tilting the look ray. The normal appears
 * quadratically, so the result does not depend on which way it points.
 */
export const projectOntoSegmentLine = (segment, point) => {
  const perp =
    (point.x - segment.center.x) * segment.normal.x +
    (point.z - segment.center.z) * segment.normal.z;
  return {
    x: point.x - perp * segment.normal.x,
    z: point.z - perp * segment.normal.z,
  };
};

/**
 * Distance from `origin` along unit direction `dir` to the first crossing of
 * the room polygon, or `Infinity` if the ray never leaves it.
 *
 * This is the headroom actually available to walk backwards from a wall.
 *
 * It replaces a `roomDepthFromSegment` helper that measured the polygon's
 * greatest reach from the wall's infinite LINE, anywhere in the room. On a
 * non-convex plan the two diverge badly: in the kitchen behind issue #1456
 * that reported 6.0 m of depth off the cabinet wall, because a side wing
 * extends that far, while the ray from the cabinets crosses the opposite wall
 * after 2.4 m — so backing the walker up by the former put them outside.
 */
export const distanceToPolygonAlongRay = (corners, origin, dir) => {
  const cross = (px, pz, qx, qz) => px * qz - pz * qx;
  let best = Infinity;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const denom = cross(dir.x, dir.z, ex, ez);
    // Parallel to this edge: it can't be the crossing that stops the ray.
    if (Math.abs(denom) < 1e-12) continue;
    const ax = a.x - origin.x;
    const az = a.z - origin.z;
    const t = cross(ax, az, ex, ez) / denom;
    const u = cross(ax, az, dir.x, dir.z) / denom;
    // `u` inside the edge, `t` strictly ahead of the origin.
    if (t > 1e-9 && u >= 0 && u <= 1 && t < best) best = t;
  }
  return best;
};

// --- object → segment attribution ------------------------------------------

export const objectCenter = (obj) => {
  if (obj?.center) return { x: obj.center.x ?? 0, z: obj.center.z ?? 0 };
  if (obj?.transform?.length === 16) {
    return { x: obj.transform[12], z: obj.transform[14] };
  }
  return null;
};

/**
 * Does this object physically stand against `segment`?
 *
 * True when the object's centre is within its OWN depth of the wall — i.e.
 * its back face is at the wall. The object's depth is the scale, so this
 * needs no tuned constant and works for a 0.3 m cabinet and a 0.9 m island
 * alike: a unit pushed against a wall measures ~half its depth from it,
 * while anything standing free is at least a walkway away.
 *
 * This exists because `parentWallIds` is an unreliable proxy for it. Scanned
 * cabinets carry the link, but GENERATED ones (kitchen layout) carry none —
 * 14 of 16 in the room behind issue #1456 — so a presence check discarded
 * two real 4-5 m cabinet runs and let a 0.74 m stub win "longest run".
 * Checked against the offline corpus: the geometric answer agrees with
 * `parentWallIds` on all 24 cabinets that carry it, and still rejects all 4
 * genuine islands (each >1.5 m clear of any wall).
 */
const standsAgainstSegment = (obj, segment) => {
  const center = objectCenter(obj);
  const depth = obj?.dimensions?.z;
  if (!center || !(depth > 0) || !segment) return false;
  return distanceToLineSegment(center, segment.p0, segment.p1) <= depth;
};

/**
 * The segment an object actually stands against — the nearest one it is in
 * contact with, by FINITE distance.
 *
 * `closestSegmentToPoint` ranks by distance to the infinite LINE, so a short
 * jog collinear with the real wall ties with it and wins on array order. That
 * matters here: filtering on the line-nearest segment would DROP a cabinet
 * that stands against a wall, instead of crediting the wall it is on.
 */
const segmentObjectStandsAgainst = (segments, obj) => {
  let best = null;
  let bestDist = Infinity;
  const center = objectCenter(obj);
  if (!center) return null;
  for (const seg of segments) {
    if (!standsAgainstSegment(obj, seg)) continue;
    const d = distanceToLineSegment(center, seg.p0, seg.p1);
    if (d < bestDist) {
      bestDist = d;
      best = seg;
    }
  }
  return best;
};

const closestSegmentToPoint = (segments, point) => {
  let best = null;
  let bestDist = Infinity;
  for (const seg of segments) {
    const d = distanceToSegmentLine(seg, point);
    if (d < bestDist) {
      bestDist = d;
      best = seg;
    }
  }
  return best;
};

const matchByParentWallId = (segments, wallIdToSegmentIndex, obj) => {
  const pwid = obj?.parentWallIds?.[0];
  if (!pwid) return null;
  const idx = wallIdToSegmentIndex.get(pwid);
  if (idx == null) return null;
  return segments[idx];
};

/**
 * Pick the "most interesting" wall for a room. Heuristic:
 *
 *   - bathroom: the wall the sink sits on (works for pedestal, wall-mount,
 *               and vanity setups). Falls back to the vanity (lower-cabinet)
 *               wall, then to the longest wall segment.
 *   - kitchen:  the wall with the longest cumulative run of lower-cabinets
 *               (upper-cabinets fall back).
 *   - other:    the longest wall segment.
 *
 * Returns `{ segment, focus, reason, fallbackUsed }`. `segment` carries
 * `{ index, p0, p1, length, center:{x,z}, normal:{x,z} (outward, unit),
 * tangent:{x,z} }`. `fallbackUsed` is true when the room-kind heuristic
 * couldn't find a fixture and we fell back to the longest-wall default.
 */
export const pickInterestingWall = (room, structureData) => {
  const segments = buildWallSegments(corners2D(room));
  if (!segments.length) {
    return { segment: null, reason: 'no-walls', fallbackUsed: true };
  }

  // Index segments by wall ID when the room exposes ordered walls. We walk
  // room.walls in declaration order and match each to its segment by
  // closest perpendicular distance from the wall center.
  const wallIdToSegmentIndex = new Map();
  for (const wall of room?.walls || []) {
    const wc = wall?.center
      ? { x: wall.center.x ?? 0, z: wall.center.z ?? 0 }
      : null;
    if (!wc) continue;
    const seg = closestSegmentToPoint(segments, wc);
    if (seg && wall?.id != null) wallIdToSegmentIndex.set(wall.id, seg.index);
  }

  // reason: `room.objects` is canonical for room-scoped fixtures (see
  // sinkHelpers/backsplashHelpers etc.). The structureData fallback below
  // matches by either `parentRoomId` (walls) or `roomId`, but the bathroom
  // fixtures emitted by assembleVanity carry neither today, so it's a
  // best-effort backstop for non-vanity emitters, not a load-bearing path.
  const roomObjects =
    room?.objects ||
    (structureData?.objects || []).filter(
      (o) => o?.parentRoomId === room?.id || o?.roomId === room?.id
    );

  const kind = primaryRoomKind(room);

  const longestSegment = () =>
    segments.reduce((a, b) => (b.length > a.length ? b : a), segments[0]);

  // Resolve an object to its best-guess wall segment. Tries parentWallIds
  // first (canonical, set on the cabinet/wall-mount); on miss, falls back
  // to the segment closest to the object's center.
  //
  // Sinks created by `assembleVanity` carry NO `parentWallIds` of their
  // own — they reference the countertop via `parents`, which references the
  // cabinet via `parents`. Rather than walk that chain, we rely on
  // closest-segment-to-sink-center, which is robust for the typical
  // wall-aligned bathroom and degrades gracefully on L-shaped rooms (same
  // behavior as the pre-existing vanity fallback).
  const resolveObjectSegment = (obj) => {
    if (!obj) return null;
    return (
      matchByParentWallId(segments, wallIdToSegmentIndex, obj) ||
      closestSegmentToPoint(segments, objectCenter(obj) || segments[0].center)
    );
  };

  if (kind === 'bathroom') {
    const sink = roomObjects.find((o) => resolveObjectType(o) === 'sink');
    if (sink) {
      const seg = resolveObjectSegment(sink);
      if (seg) {
        return {
          segment: seg,
          focus: objectCenter(sink) || seg.center,
          reason: 'bathroom-sink',
          fallbackUsed: false,
        };
      }
    }
    const vanity = roomObjects.find(
      (o) => resolveObjectType(o) === 'lower-cabinet'
    );
    if (vanity) {
      const seg = resolveObjectSegment(vanity);
      if (seg) {
        return {
          segment: seg,
          focus: objectCenter(vanity) || seg.center,
          reason: 'bathroom-vanity',
          fallbackUsed: false,
        };
      }
    }
    const lseg = longestSegment();
    return {
      segment: lseg,
      focus: lseg.center,
      reason: 'bathroom-longest',
      fallbackUsed: true,
    };
  }

  if (kind === 'kitchen') {
    // Group cabinets by their best-matching wall segment and sum widths.
    const runByIndex = new Map();
    const addRun = (segIndex, length) => {
      runByIndex.set(segIndex, (runByIndex.get(segIndex) || 0) + length);
    };
    const cabinetWidth = (obj) => {
      const d = obj?.dimensions;
      if (!d) return 0;
      // Cabinets advertise width along their wall in `x`.
      return Math.max(d.x ?? 0, d.width ?? 0, 0);
    };
    // Track which walls have UPPER cabinets — the kitchen "back wall" with
    // both lower + upper cabinets is the visually meaningful framing
    // target, not an island (which never has upper cabinets) or a side run
    // of lower cabinets only.
    const hasUpperByIndex = new Set();
    for (const obj of roomObjects) {
      if (resolveObjectType(obj) !== 'upper-cabinet') continue;
      const seg = resolveObjectSegment(obj);
      if (seg) hasUpperByIndex.add(seg.index);
    }
    for (const obj of roomObjects) {
      if (resolveObjectType(obj) !== 'lower-cabinet') continue;
      // An explicit `parentWallIds` link names the wall. Without one, find
      // the wall the cabinet physically stands against — generated cabinets
      // never carry the link, and treating that as "island" discarded whole
      // runs (issue #1456). A unit against no wall IS an island, and counting
      // it would inflate a run it isn't part of.
      const seg =
        (obj?.parentWallIds?.length > 0 &&
          matchByParentWallId(segments, wallIdToSegmentIndex, obj)) ||
        segmentObjectStandsAgainst(segments, obj);
      if (!seg) continue;
      addRun(seg.index, cabinetWidth(obj) || seg.length * 0.001);
    }
    if (!runByIndex.size) {
      for (const obj of roomObjects) {
        if (resolveObjectType(obj) !== 'upper-cabinet') continue;
        const seg = resolveObjectSegment(obj);
        if (!seg) continue;
        addRun(seg.index, cabinetWidth(obj) || seg.length * 0.001);
      }
    }
    // Walls that have a sink on them are de-prioritized — the user wants to
    // look at a cabinet run, not the sink. Map each sink to its wall
    // segment and exclude those segment indices from the candidate set
    // unless excluding them leaves nothing.
    const sinkSegIndices = new Set();
    for (const obj of roomObjects) {
      if (resolveObjectType(obj) !== 'sink') continue;
      const seg =
        matchByParentWallId(segments, wallIdToSegmentIndex, obj) ||
        closestSegmentToPoint(
          segments,
          objectCenter(obj) || segments[0].center
        );
      if (seg) sinkSegIndices.add(seg.index);
    }
    const pickFrom = (filterOutSinks, requireUpper) => {
      let bestIdx = -1;
      let bestRun = -1;
      for (const [idx, run] of runByIndex) {
        if (filterOutSinks && sinkSegIndices.has(idx)) continue;
        if (requireUpper && !hasUpperByIndex.has(idx)) continue;
        if (run > bestRun) {
          bestRun = run;
          bestIdx = idx;
        }
      }
      return bestIdx;
    };
    if (runByIndex.size) {
      // Preference order: (1) wall with both lower+upper cabinets and no
      // sink, (2) any wall with both lower+upper, (3) longest lower-run
      // excluding sink walls, (4) any longest lower-run.
      const bestIdx =
        pickFrom(true, true) >= 0
          ? pickFrom(true, true)
          : pickFrom(false, true) >= 0
            ? pickFrom(false, true)
            : pickFrom(true, false) >= 0
              ? pickFrom(true, false)
              : pickFrom(false, false);
      if (bestIdx >= 0) {
        // Focus on the weighted centroid of cabinets that landed on the
        // picked wall — that's the visual center of the run, not the
        // geometric middle of the wall segment.
        let fx = 0;
        let fz = 0;
        let wsum = 0;
        for (const obj of roomObjects) {
          const ot = resolveObjectType(obj);
          if (ot !== 'lower-cabinet' && ot !== 'upper-cabinet') continue;
          const seg =
            matchByParentWallId(segments, wallIdToSegmentIndex, obj) ||
            closestSegmentToPoint(
              segments,
              objectCenter(obj) || segments[0].center
            );
          if (!seg || seg.index !== bestIdx) continue;
          const c = objectCenter(obj);
          if (!c) continue;
          const w = Math.max(cabinetWidth(obj), 0.1);
          fx += c.x * w;
          fz += c.z * w;
          wsum += w;
        }
        const focus =
          wsum > 0 ? { x: fx / wsum, z: fz / wsum } : segments[bestIdx].center;
        return {
          segment: segments[bestIdx],
          focus,
          reason: 'kitchen-longest-cabinet-run',
          fallbackUsed: false,
        };
      }
    }
    const klseg = longestSegment();
    return {
      segment: klseg,
      focus: klseg.center,
      reason: 'kitchen-longest-wall',
      fallbackUsed: true,
    };
  }

  if (kind === 'livingroom') {
    // A fireplace is the room's focal point when present; a television is
    // the modern stand-in. Both are wall-anchored, so the wall they sit on
    // is the one worth facing.
    for (const [type, reason] of [
      ['fireplace', 'livingroom-fireplace'],
      ['television', 'livingroom-television'],
    ]) {
      const obj = roomObjects.find((o) => resolveObjectType(o) === type);
      if (!obj) continue;
      const seg = resolveObjectSegment(obj);
      if (!seg) continue;
      return {
        segment: seg,
        focus: objectCenter(obj) || seg.center,
        reason,
        fallbackUsed: false,
      };
    }
    const lvseg = longestSegment();
    return {
      segment: lvseg,
      focus: lvseg.center,
      reason: 'livingroom-longest',
      fallbackUsed: true,
    };
  }

  if (kind === 'bedroom') {
    const bed = roomObjects.find((o) => resolveObjectType(o) === 'bed');
    if (bed) {
      const seg = resolveObjectSegment(bed);
      if (seg) {
        return {
          segment: seg,
          focus: objectCenter(bed) || seg.center,
          reason: 'bedroom-bed',
          fallbackUsed: false,
        };
      }
    }
    const bdseg = longestSegment();
    return {
      segment: bdseg,
      focus: bdseg.center,
      reason: 'bedroom-longest',
      fallbackUsed: true,
    };
  }

  const olseg = longestSegment();
  return {
    segment: olseg,
    focus: olseg.center,
    reason: 'longest-wall',
    fallbackUsed: false,
  };
};
